
/**
 * BoCapital WebSocket Manager
 * Manages a single WS connection with auto-reconnect.
 */

import { authService } from './auth';

type MessageHandler = (data: any) => void;

interface WSConfig {
  url: string;
  reconnectDelay: number;
  maxReconnectDelay: number;
  maxRetries: number;
}

export class WebSocketManager {
  private ws: WebSocket | null = null;
  private config: WSConfig;
  private listeners = new Map<string, Set<MessageHandler>>();
  private globalListeners = new Set<MessageHandler>();
  private retries = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionallyClosed = false;

  constructor() {
    const protocol = typeof window !== 'undefined'
      ? (window.location.protocol === 'https:' ? 'wss:' : 'ws:')
      : 'wss:';
    const host = typeof window !== 'undefined' ? window.location.host : '';

    // In production (Cloud Run), the reverse proxy routes /ws to the WS server.
    // In local dev, the webpack devServer proxy handles /ws -> localhost:8766.
    // Override via ?ws_url= query param or WS_URL env var for custom setups.
    let wsUrl = `${protocol}//${host}/ws`;
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const override = params.get('ws_url');
      if (override) wsUrl = override;
    }
    this.config = {
      url: wsUrl,
      reconnectDelay: 1000,
      maxReconnectDelay: 30000,
      maxRetries: 20,
    };
  }

  /** Connect (or reconnect) */
  connect(): void {
    // don't attempt to create a new socket if one is already open or
    // still in the process of connecting.  the previous implementation
    // only checked for OPEN which meant a pending CONNECTING socket
    // would be abandoned and eventually fire `onclose`, flipping the
    // app state to "disconnected" even though a successful connection
    // was imminent.
    if (this.ws && (this.ws.readyState === WebSocket.OPEN ||
                    this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    // clear any pending reconnect attempt now that we're actively
    // starting a connection; otherwise the old timer could fire after
    // a successful open and replace the socket again.  this was causing
    // brief disconnection flashes in the UI.
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.intentionallyClosed = false;

    try {
      this.ws = new WebSocket(this.config.url);

      this.ws.onopen = () => {
        this.retries = 0;
        this.emit('__ws_connected', { connected: true });
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // Route by type field if present
          if (data.type) this.emit(data.type, data);
          // Also notify global listeners
          this.globalListeners.forEach((fn) => fn(data));
        } catch { /* ignore parse errors */ }
      };

      this.ws.onclose = () => {
        this.emit('__ws_disconnected', { connected: false });
        if (!this.intentionallyClosed) this.scheduleReconnect();
      };

      this.ws.onerror = () => {
        // onclose will fire after onerror — reconnect handled there
      };
    } catch {
      this.scheduleReconnect();
    }
  }

  /** Disconnect intentionally */
  disconnect(): void {
    this.intentionallyClosed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws?.close();
    this.ws = null;
  }

  /** Subscribe to a specific message type */
  on(type: string, handler: MessageHandler): () => void {
    if (!this.listeners.has(type)) this.listeners.set(type, new Set());
    this.listeners.get(type)!.add(handler);
    return () => this.listeners.get(type)?.delete(handler);
  }

  /** Subscribe to ALL messages */
  onAny(handler: MessageHandler): () => void {
    this.globalListeners.add(handler);
    return () => this.globalListeners.delete(handler);
  }

  /** Send a message */
  send(data: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  // ── Internals ─────────────────────────────────────────────────────────

  private emit(type: string, data: any): void {
    this.listeners.get(type)?.forEach((fn) => fn(data));
  }

  private scheduleReconnect(): void {
    if (this.retries >= this.config.maxRetries) return;
    const delay = Math.min(
      this.config.reconnectDelay * Math.pow(1.5, this.retries),
      this.config.maxReconnectDelay,
    );
    this.retries++;
    // if we are about to schedule a reconnect make sure the previous
    // timer is cleared so we never end up with multiple pending calls
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }
}

export const wsManager = new WebSocketManager();
export default wsManager;
